'use client'

import type { LiveInventoryReturnValues } from '@/types/api/live-inventory'

import {
  BondStatus,
  IdentifierType,
} from '@moment/trading-types/reference-data'
import { useMemo } from 'react'

import SummaryBannerGrid from '@/components/atomic/summary-banner-grid'
import Tabs from '@/components/atomic/tabs'
import { RatingBadge } from '@/components/molecules/badges/ratings'
import BondHeaderDetails from '@/components/molecules/bond-header-details'
import OrderBook from '@/components/molecules/order-book'
import PriceChart from '@/components/molecules/price-chart'
import { useGetInstrumentFromLiveInventory } from '@/hooks/use-get-instrument-from-live-inventory'
import { useReferenceDataPermissions } from '@/hooks/use-reference-data-permissions'
import { useUserRiskGroupId } from '@/hooks/use-user-risk-group-id'
import {
  getIdentifierPreference,
  IdentifierPreference,
} from '@/shared/helpers/identifier-preference'
import { formatPrice } from '@/utils/presentation/formatting'
import { referenceDataTransformationConfig } from '@/utils/presentation/instrument-data-transforms'

enum BondPreviewTabsTop {
  Chart = 'Chart',
  BondDetails = 'Bond details',
  CorporateAction = 'Corporate action',
}

enum BondPreviewTabsBottom {
  OrderBook = 'Order book',
  TraceData = 'TRACE data',
}

interface BondPreviewProps {
  id: string
}

const BOND_PREVIEW_RETURNS: LiveInventoryReturnValues[] = [
  'close_ytw',
  'cusip',
  'description_short',
  'duration',
  'isin',
  'eval_price',
  'issuer',
  'fitch_rating',
  'moodys_rating',
  'next_coupon_date',
  'sp_rating',
  'type',
]

function BondPreview({ id }: BondPreviewProps) {
  const { riskGroupId } = useUserRiskGroupId()

  const { instrument: instrumentDetails, isLoading } =
    useGetInstrumentFromLiveInventory({
      identifier: id,
      identifierType: IdentifierType.ISIN,
      riskGroupId,
      returns: BOND_PREVIEW_RETURNS,
      filters: {
        status: { in: [BondStatus.Outstanding, BondStatus.PreIssuance] },
      },
    })

  const {
    isin,
    cusip,
    issuer,
    eval_price,
    close_ytw,
    duration,
    next_coupon_date,
    sp_rating,
    moodys_rating,
    fitch_rating,
    description_short,
    type,
  } = instrumentDetails ?? {}

  const { hasSPAccess, hasMoodysAccess, hasFitchAccess } =
    useReferenceDataPermissions()

  const identifier = useMemo(() => {
    const preference = getIdentifierPreference()
    return preference === IdentifierPreference.ISIN ? isin : cusip
  }, [isin, cusip])

  if (!instrumentDetails || isLoading) return null

  // Determine the appropriate rating header based on permissions and data availability
  const getRatingHeader = () => {
    const hasSpData = hasSPAccess && sp_rating
    const hasMoodysData = hasMoodysAccess && moodys_rating
    const hasFitchData = hasFitchAccess && fitch_rating

    const parts: string[] = []
    if (hasMoodysData) parts.push("Moody's")
    if (hasSpData) parts.push('S&P')
    if (hasFitchData) parts.push('Fitch')

    return parts.length > 0 ? parts.join('/') : 'Rating'
  }

  const evalPrice = eval_price ?? null

  const summaryItems = [
    { label: 'Issuer', value: issuer ?? '' },
    {
      label: 'Eval value',
      value: formatPrice(evalPrice, {
        useZeroPlaceholder: true,
      }),
    },
    {
      label: 'YTW',
      value:
        referenceDataTransformationConfig.close_ytw.valueTransformation(
          close_ytw ?? null,
        ) ?? '',
    },
    {
      label: 'Duration',
      value:
        referenceDataTransformationConfig.duration.valueTransformation(
          duration ?? null,
        ) ?? '',
    },
    {
      label: 'Next Coupon',
      value:
        referenceDataTransformationConfig.next_coupon_date.valueTransformation(
          next_coupon_date ?? null,
        ) ?? '',
    },
    {
      label: getRatingHeader(),
      value: (
        <RatingBadge
          // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
          spRating={hasSPAccess ? (sp_rating as any) : undefined}
          // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
          moodysRating={hasMoodysAccess ? (moodys_rating as any) : undefined}
          fitchRating={hasFitchAccess ? (fitch_rating ?? undefined) : undefined}
        />
      ),
    },
  ]

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 py-2">
        <BondHeaderDetails
          bondTitle={description_short ?? ''}
          id={identifier ?? id}
          bondType={type}
        />
      </div>
      <div className="shrink-0 px-4 pb-3">
        <SummaryBannerGrid
          columns={summaryItems.length}
          summaryItems={summaryItems}
        />
      </div>
      <div className="grow overflow-y-auto flex flex-col">
        <div className="min-h-0 flex shrink-0 flex-col">
          <Tabs
            baseTabsClassName="border-t border-primary-border"
            tabListClassName="pl-[16px]"
            defaultTab={BondPreviewTabsTop.Chart}
            value={BondPreviewTabsTop.Chart}
            tabs={[BondPreviewTabsTop.Chart]}
            onTabChange={() => {}}
          />
          <PriceChart isin={id} height={250} />
        </div>

        <div className="min-h-0 flex grow flex-col">
          <Tabs
            baseTabsClassName="border-t border-primary-border"
            tabListClassName="pl-[16px]"
            defaultTab={BondPreviewTabsBottom.OrderBook}
            value={BondPreviewTabsBottom.OrderBook}
            tabs={[BondPreviewTabsBottom.OrderBook]}
            onTabChange={() => {}}
          />
          <div className="overflow-y-auto h-full bg-secondary-background">
            <OrderBook
              instrumentId={id}
              baseClassName="h-full"
              collapsible={false}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default BondPreview
